<template>
    <div>
        <div v-if="detail">
            <!-- Maklumat pendaftaran -->
            <div class="card mb-3">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start flex-wrap mb-2">
                        <h5 class="mb-1">Maklumat Pendaftaran</h5>
                        <span class="badge" :class="statusBadge">{{ detail.status_label }}</span>
                    </div>

                    <div class="mb-3" :dir="labels.dir" :class="{ 'arabic-text': isArabic }">
                        <small class="text-muted d-block">{{ labels.title }}</small>
                        <strong>{{ detail.title || '-' }}</strong>
                    </div>

                    <div class="row">
                        <div class="col-12 col-md-4 mb-2">
                            <small class="text-muted d-block">Pensyarah Pembimbing</small>
                            <span :class="{ 'arabic-text': isArabic }" :dir="isArabic ? 'rtl' : 'auto'">{{ supervisorName }}</span>
                        </div>
                        <div class="col-6 col-md-4 mb-2">
                            <small class="text-muted d-block">Semester / Tahun</small>
                            Semester {{ detail.intake_semester }} / {{ detail.intake_year }}
                            <small
                                v-if="detail.semester !== detail.intake_semester || detail.year !== detail.intake_year"
                                class="text-muted d-block"
                            >Semester markah: {{ detail.semester }} / {{ detail.year }}</small>
                        </div>
                        <div class="col-6 col-md-4 mb-2">
                            <small class="text-muted d-block">Cubaan</small>
                            {{ detail.attempt }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Status Bab -->
            <div v-if="detail.chapters && detail.chapters.length" class="card mb-3">
                <div class="card-body" :dir="labels.dir" :class="{ 'arabic-text': isArabic }">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <h5 class="mb-0">{{ labels.chapters }}</h5>
                        <small class="text-muted" dir="ltr">{{ chaptersDone }} / {{ detail.chapters.length }}</small>
                    </div>
                    <div class="progress mb-3" style="height: 8px;" dir="ltr">
                        <div class="progress-bar bg-success" :style="{ width: chapterPercent + '%' }"></div>
                    </div>
                    <ul class="list-unstyled mb-0">
                        <li v-for="chapter in detail.chapters" :key="chapter.id" class="d-flex align-items-center mb-1">
                            <i
                                class="mx-2"
                                :class="chapter.completed_at ? 'fas fa-check-circle text-success' : 'far fa-circle text-muted'"
                            ></i>
                            <span class="flex-grow-1">{{ isArabic ? chapter.name_ar : chapter.name }}</span>
                            <small v-if="chapter.completed_at" class="text-muted" dir="ltr">{{ chapter.completed_at }}</small>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Jadual Kemajuan -->
            <div class="card mb-3">
                <div class="card-body" :dir="labels.dir" :class="{ 'arabic-text': isArabic }">
                    <h5 class="mb-3">{{ labels.kemajuan }}</h5>

                    <div v-if="!detail.progress_logs.length" class="text-muted">
                        <i class="fas fa-inbox"></i> Tiada catatan perjumpaan lagi
                    </div>

                    <template v-else>
                        <!-- Desktop: jadual -->
                        <div class="table-responsive d-none d-md-block">
                            <table class="table table-bordered table-sm mb-0">
                                <thead class="thead-light">
                                    <tr>
                                        <th class="text-center">{{ labels.no }}</th>
                                        <th>{{ labels.date }}</th>
                                        <th>{{ labels.discussed }}</th>
                                        <th>{{ labels.studentProgress }}</th>
                                        <th>{{ labels.supervisorComment }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(log, i) in detail.progress_logs" :key="log.id">
                                        <td class="text-center">{{ i + 1 }}</td>
                                        <td class="text-nowrap" dir="ltr">{{ log.meeting_date }}</td>
                                        <td class="pre-line">{{ log.discussed }}</td>
                                        <td class="pre-line">{{ log.student_progress || '-' }}</td>
                                        <td class="pre-line">
                                            {{ log.supervisor_comment || '-' }}
                                            <small class="text-muted d-block mt-1">— {{ teacherName(log) }}</small>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Mudah alih: kad setiap perjumpaan -->
                        <div class="d-md-none">
                            <div v-for="(log, i) in detail.progress_logs" :key="log.id" class="border rounded p-2 mb-2">
                                <div class="d-flex justify-content-between mb-1">
                                    <strong>#{{ i + 1 }}</strong>
                                    <small class="text-muted" dir="ltr">{{ log.meeting_date }}</small>
                                </div>
                                <small class="text-muted d-block">{{ labels.discussed }}</small>
                                <p class="pre-line mb-1">{{ log.discussed }}</p>
                                <small class="text-muted d-block">{{ labels.studentProgress }}</small>
                                <p class="pre-line mb-1">{{ log.student_progress || '-' }}</p>
                                <small class="text-muted d-block">{{ labels.supervisorComment }}</small>
                                <p class="pre-line mb-1">{{ log.supervisor_comment || '-' }}</p>
                                <small class="text-muted">— {{ teacherName(log) }}</small>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Lain-lain catatan / Ulasan Akhir -->
            <div v-if="detail.other_notes || detail.final_comment" class="card mb-3">
                <div class="card-body" :dir="labels.dir" :class="{ 'arabic-text': isArabic }">
                    <div v-if="detail.other_notes" class="mb-3">
                        <h6 class="font-weight-bold">{{ labels.otherNotes }}</h6>
                        <p class="pre-line mb-0">{{ detail.other_notes }}</p>
                    </div>
                    <div v-if="detail.final_comment">
                        <h6 class="font-weight-bold">{{ labels.finalComment }}</h6>
                        <p class="pre-line mb-0">{{ detail.final_comment }}</p>
                    </div>
                </div>
            </div>

            <!-- Soft copy -->
            <div class="card mb-3">
                <div class="card-body">
                    <h5 class="mb-3">Soft Copy Latihan Ilmiah</h5>

                    <ul v-if="detail.files.length" class="list-group mb-3">
                        <li
                            class="list-group-item d-flex align-items-center flex-wrap"
                            v-for="file in detail.files"
                            :key="file.id"
                        >
                            <i class="fas fa-file-pdf text-danger mr-2"></i>
                            <div class="flex-grow-1">
                                <span class="d-block text-break">{{ file.original_name }}</span>
                                <small class="text-muted">
                                    {{ formatSize(file.size) }} · {{ file.created_at }}
                                    · {{ file.uploaded_by === 'student' ? 'Dimuat naik oleh anda' : 'Dimuat naik oleh pensyarah' }}
                                </small>
                                <small v-if="file.note" class="text-muted d-block">{{ file.note }}</small>
                            </div>
                            <button
                                class="btn btn-sm btn-primary ml-2"
                                :disabled="busyFileId === file.id"
                                @click="download(file)"
                            >
                                <i class="fas fa-download"></i> Muat Turun
                            </button>
                            <button
                                v-if="detail.can_upload && file.is_own"
                                class="btn btn-sm btn-outline-danger ml-2"
                                :disabled="busyFileId === file.id"
                                @click="remove(file)"
                            >
                                <i class="fas fa-trash"></i>
                            </button>
                        </li>
                    </ul>
                    <p v-else class="text-muted">Tiada fail dimuat naik</p>

                    <!-- Muat naik (berdaftar / dinilai sahaja) -->
                    <form v-if="detail.can_upload" @submit.prevent="upload">
                        <div class="form-group mb-2">
                            <input
                                ref="fileInput"
                                type="file"
                                accept="application/pdf,.pdf"
                                class="form-control-file"
                                @change="onFileChange"
                            />
                            <small class="text-muted">PDF sahaja, maksimum 20 MB</small>
                        </div>
                        <div class="form-group mb-2">
                            <input
                                v-model="note"
                                type="text"
                                maxlength="500"
                                class="form-control form-control-sm"
                                placeholder="Catatan (pilihan)"
                            />
                        </div>
                        <button type="submit" class="btn btn-sm btn-success" :disabled="!selectedFile || uploading">
                            <i class="fas fa-upload"></i> {{ uploading ? 'Sila tunggu…' : 'Muat Naik' }}
                        </button>
                    </form>
                </div>
            </div>
        </div>

        <div v-else-if="!loading" class="empty-state-sm">
            <i class="fas fa-exclamation-circle"></i>
            <p>Latihan Ilmiah tidak dijumpai</p>
        </div>
    </div>
</template>

<script>
import labelsFor from './labels'

const MAX_FILE_SIZE = 20 * 1024 * 1024   // 20 MB — sama dengan backend

export default {
    name: 'LatihanIlmiahRegistrationDetail',
    props: {
        registrationId: { type: String, required: true },
    },
    data() {
        return {
            loading: false,
            uploading: false,
            busyFileId: null,
            selectedFile: null,
            note: '',
        }
    },
    computed: {
        detail() {
            return this.$store.state.latihanIlmiah.details[this.registrationId] || null
        },
        isArabic() {
            return this.detail && this.detail.form_language === 'ar'
        },
        labels() {
            return labelsFor(this.detail ? this.detail.form_language : 'ms')
        },
        supervisorName() {
            return this.isArabic ? this.detail.supervisor_name_ar : this.detail.supervisor_name
        },
        statusBadge() {
            return {
                berdaftar: 'badge-primary',
                dinilai: 'badge-warning',
                disinkron: 'badge-success',
            }[this.detail.status] || 'badge-secondary'
        },
        chaptersDone() {
            return this.detail.chapters.filter(chapter => chapter.completed_at).length
        },
        chapterPercent() {
            return Math.round((this.chaptersDone / this.detail.chapters.length) * 100)
        },
    },
    mounted() {
        this.loadDetail()
    },
    methods: {
        async loadDetail() {
            this.loading = true
            this.$Progress.start()
            try {
                await this.$store.dispatch('latihanIlmiah/fetchRegistration', this.registrationId)
                this.$Progress.finish()
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.loading = false
            }
        },

        teacherName(log) {
            return this.isArabic ? log.teacher_name_ar : log.teacher_name
        },

        onFileChange(event) {
            const file = event.target.files[0] || null
            if (file && file.size > MAX_FILE_SIZE) {
                this.$swal('Ralat', 'Saiz fail tidak boleh melebihi 20 MB', 'error')
                this.resetFileInput()
                return
            }
            this.selectedFile = file
        },

        resetFileInput() {
            this.selectedFile = null
            if (this.$refs.fileInput) this.$refs.fileInput.value = ''
        },

        async upload() {
            this.uploading = true
            this.$Progress.start()
            try {
                await this.$store.dispatch('latihanIlmiah/uploadFile', {
                    id: this.registrationId,
                    file: this.selectedFile,
                    note: this.note,
                })
                this.$Progress.finish()
                this.resetFileInput()
                this.note = ''
                this.$swal('Berjaya', 'Fail berjaya dimuat naik', 'success')
            } catch (error) {
                this.handleApiError(error)
                this.$swal('Ralat', this.validationMessage(error) || 'Gagal memuat naik fail', 'error')
            } finally {
                this.uploading = false
            }
        },

        async remove(file) {
            const result = await this.$swal.fire({
                title: 'Padam fail?',
                text: file.original_name,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Ya, padam',
                cancelButtonText: 'Batal',
            })
            if (!result.isConfirmed) return

            this.busyFileId = file.id
            this.$Progress.start()
            try {
                await this.$store.dispatch('latihanIlmiah/deleteFile', { id: this.registrationId, fileId: file.id })
                this.$Progress.finish()
            } catch (error) {
                this.handleApiError(error)
                this.$swal('Ralat', this.validationMessage(error) || 'Gagal memadam fail', 'error')
            } finally {
                this.busyFileId = null
            }
        },

        async download(file) {
            this.busyFileId = file.id
            this.$Progress.start()
            try {
                const blob = await this.$store.dispatch('latihanIlmiah/downloadFile', {
                    id: this.registrationId,
                    fileId: file.id,
                })
                this.saveBlob(blob, file.original_name)
                this.$Progress.finish()
            } catch (error) {
                this.handleApiError(error)
                this.$swal('Ralat', 'Gagal memuat turun fail', 'error')
            } finally {
                this.busyFileId = null
            }
        },

        saveBlob(blob, filename) {
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename || 'latihan-ilmiah.pdf'
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)
        },

        // Mesej 422 pertama dari Laravel (validation / status pendaftaran)
        validationMessage(error) {
            const data = error.response && error.response.data
            if (data && data.errors) {
                const first = Object.values(data.errors)[0]
                return Array.isArray(first) ? first[0] : first
            }
            return data && data.message
        },

        formatSize(bytes) {
            if (!bytes) return '-'
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB'
            return (bytes / 1024 / 1024).toFixed(1) + ' MB'
        },

        handleApiError(error) {
            this.$Progress.fail()
            if (error.response && (error.response.status === 401 || error.response.status === 419)) {
                this.$store.dispatch('authentication/cencelAuthen')
            }
            // eslint-disable-next-line no-console
            console.log(error)
        },
    },
}
</script>

<style scoped>
.pre-line {
    white-space: pre-line;
}
.arabic-text {
    font-family: 'AmiriFont', 'Amiri', serif;
}
</style>
